import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { getError } from '@/utils/hookFormUtils';

import type { TooltipProps } from '@mui/material/Tooltip';

interface SelectFieldProps {
  label: string;
  name: string;
  options: { value: string; label: string }[];
  errors?: any;
  value: any;
  onChange: any;
  sxLabel?: any;
  sxSelect?: any;
  variant?: 'standard' | 'outlined' | 'filled';
  tooltipText?: string;
  tooltipLink?: string;
  icon?: React.ReactNode;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const CssSelect = styled(Select)(({ theme }) => ({
  '& label.Mui-focused': {
    color: theme.palette.primary.main,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  name,
  options,
  value,
  onChange,
  errors,
  sxLabel,
  sxSelect,
  variant,
  tooltipText,
  tooltipLink,
  icon,
  placeholder,
  size = 'medium',
  disabled,
}) => {
  const theme = useTheme();
  const error = getError(errors, name);
  return (
    <Stack width={'100%'}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0rem' }}>
        {icon && (
          <Icon color="disabled" sx={{ marginRight: '0.5rem' }}>
            {icon}
          </Icon>
        )}

        <Typography
          variant="body1"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.grey[500],
            margin: '1rem 0.5rem 0.5rem 0',
            ...sxLabel,
          }}
        >
          {label}
        </Typography>

        {tooltipText && (
          <HtmlTooltip
            title={
              <Typography variant="body2">
                {tooltipText}{' '}
                {tooltipLink && (
                  <a href={tooltipLink} target="__blank">
                    Ver más
                  </a>
                )}
              </Typography>
            }
          >
            <HelpOutlineIcon color="primary" />
          </HtmlTooltip>
        )}
      </Box>
      <FormControl sx={{ width: '100%' }} error={!!error?.message} data-testid={label}>
        <CssSelect
          value={value}
          onChange={onChange}
          sx={{ ...sxSelect }}
          variant={variant}
          size={size}
          disabled={disabled}
          defaultValue={options.length > 0 ? options[0].value : ''}
          displayEmpty
        >
          <MenuItem value="" disabled>
            {placeholder || 'Selecciona una opción'}
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </CssSelect>
        {!!error && <Typography color="error">{(error?.message as string) ?? ''}</Typography>}
      </FormControl>
    </Stack>
  );
};

export default SelectField;
